import { registerValidator } from './validators.js';
import { isServerDataType, isActionType, migrateRuleConfig, isBalanceTrigger } from './rule-attachment.js';
import { classifyField } from './field-classifier.js';
// ===========================================================================
// Compatibility matrices
// ===========================================================================
/**
 * For each field dataType, the set of comparison operators that are
 * semantically valid. Operators outside this set produce a warn-severity
 * `condition.operator.invalid` finding when used.
 *
 * Notes on edge calls:
 * - `gt/lt/ge/le` on text: lexicographic compare exists at runtime but is
 *   rarely the intended business semantic after a `currency → text`
 *   transition. Warned.
 * - `matches_regex` on non-text: dates/numbers aren't strings; runtime
 *   would coerce-then-match, producing surprising results. Warned.
 * - `is_empty` / `is_not_empty`: type-agnostic null-check. Allowed everywhere.
 * - `in` / `not_in`: set membership; allowed everywhere (value-side type
 *   check is a v2 follow-up).
 */
export const OPERATOR_COMPATIBILITY = {
    text: ['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with', 'in', 'not_in', 'is_empty', 'is_not_empty', 'matches_regex'],
    number: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_or_equal', 'less_or_equal', 'in', 'not_in', 'is_empty', 'is_not_empty', 'between'],
    decimal: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_or_equal', 'less_or_equal', 'in', 'not_in', 'is_empty', 'is_not_empty', 'between'],
    // Currency stores like decimal — same comparison surface; formatting is render-time only.
    currency: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_or_equal', 'less_or_equal', 'in', 'not_in', 'is_empty', 'is_not_empty', 'between'],
    date: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_or_equal', 'less_or_equal', 'in', 'not_in', 'is_empty', 'is_not_empty', 'between'],
    boolean: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
    // Lookup persists the lookup-table return column's value (typically a string id).
    // Compared as text — substring/regex semantics make sense for free-form keys.
    lookup: ['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with', 'in', 'not_in', 'is_empty', 'is_not_empty', 'matches_regex'],
    // Dropdown persists the selected option's `value` (always a string).
    // Same surface as lookup/text.
    dropdown: ['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with', 'in', 'not_in', 'is_empty', 'is_not_empty', 'matches_regex'],
};
/**
 * For each field_group aggregate kind, the set of dataTypes that aggregate
 * accepts as input. Members outside this set produce an error-severity
 * `condition.field-group.aggregate.invalid` finding. `'any'` means the
 * aggregate is type-agnostic.
 */
export const AGGREGATE_REQUIRES_TYPE = {
    count_checked: ['boolean'],
    count_filled: 'any',
    sum: ['number', 'decimal'],
    any: ['boolean'],
    all: ['boolean'],
    none: ['boolean'],
};
/**
 * For each field_group aggregate kind, the dataType of the aggregate's
 * RESULT value. Used by the operator-validity check on field_group sources
 * (Layer 2 of the design's two-layer model).
 */
export const AGGREGATE_RESULT_TYPE = {
    count_checked: 'number',
    count_filled: 'number',
    sum: 'number',
    any: 'boolean',
    all: 'boolean',
    none: 'boolean',
};
// ===========================================================================
// Walker
// ===========================================================================
/**
 * Recursively walks a ConditionGroup, visiting every SingleCondition leaf
 * with its full path context. Path elements appended as the walker
 * descends: `'conditions'`, then numeric index, then (for nested groups)
 * the same pattern recursively.
 */
export function walkConditionGroup(group, basePath, visit) {
    group.conditions.forEach((c, idx) => {
        const path = [...basePath, 'conditions', idx];
        if ('logic' in c) {
            walkConditionGroup(c, path, visit);
        }
        else {
            visit(c, path);
        }
    });
}
/**
 * Iterates all rule conditions and routing conditions in the content,
 * calling visit with discriminated paths.
 *
 * Path discriminator:
 * - Rule conditions: ['rules', rule.id, 'condition', ...walker-path]
 * - Routing conditions: ['export', 'destinations', dest.id, 'routingCondition', ...walker-path]
 */
export function walkAllConditions(content, visit) {
    for (const rule of content.rules) {
        if (rule.condition) {
            walkConditionGroup(rule.condition, ['rules', rule.id, 'condition'], visit);
        }
    }
    if (content.export?.destinations) {
        for (const dest of content.export.destinations) {
            if (dest.routingCondition) {
                walkConditionGroup(dest.routingCondition, ['export', 'destinations', dest.id, 'routingCondition'], visit);
            }
        }
    }
}
// ===========================================================================
// Field index
// ===========================================================================
export function buildFieldIndex(fields) {
    return new Map(fields.map(f => [f.name, f]));
}
// ===========================================================================
// Surface-description helper (used in error messages)
// ===========================================================================
export function describeSurface(content, path) {
    if (path[0] === 'rules') {
        const ruleId = path[1];
        const rule = content.rules.find(r => r.id === ruleId);
        return `Rule "${rule?.name ?? '(unknown)'}"`;
    }
    if (path[0] === 'export') {
        const destId = path[2];
        const dest = content.export.destinations.find(d => d.id === destId);
        return `Routing condition for destination "${dest?.name ?? '(unknown)'}"`;
    }
    return 'Condition';
}
// ===========================================================================
// Validator: condition.field.missing (error)
// ===========================================================================
function conditionFieldMissingValidator(content, _opts) {
    const out = [];
    const fieldIndex = buildFieldIndex(content.fields);
    walkAllConditions(content, (cond, path) => {
        const surfaceLabel = describeSurface(content, path);
        out.push(...checkFieldMissingOnCondition(cond, path, fieldIndex, surfaceLabel));
    });
    return out;
}
// ===========================================================================
// Helpers shared by operator.invalid + value.type-mismatch
// ===========================================================================
export const NO_VALUE_OPERATORS = new Set(['is_empty', 'is_not_empty']);
/**
 * Resolves the dataType of a condition's source for compatibility checks.
 * - 'field' source: the field's dataType from the index, or null if missing.
 * - 'field_group' source: the aggregate's result type (Layer 2 of design).
 * - Other sources (expression, document, batch, lookup): null in v1; no
 *   static dataType to compare against. Future v2 follow-ups may add typing
 *   hints to these.
 */
export function dataTypeOfSource(source, fieldIndex) {
    if (source.type === 'field') {
        const f = fieldIndex.get(source.name);
        return f?.dataType ?? null;
    }
    if (source.type === 'field_group') {
        return AGGREGATE_RESULT_TYPE[source.aggregate] ?? null;
    }
    if (source.type === 'confidence')
        return 'boolean';
    return null;
}
export function describeFieldOf(source) {
    if (source.type === 'field')
        return `field "${source.name}"`;
    if (source.type === 'field_group') {
        return `field group [${source.names.join(', ')}] (aggregate "${source.aggregate}")`;
    }
    if (source.type === 'confidence') {
        return source.scope === 'field' ? `confidence of field "${source.name}"` : 'confidence of any field';
    }
    return 'source';
}
export function fieldNameOf(source) {
    if (source.type === 'field')
        return source.name;
    if (source.type === 'field_group')
        return source.names[0];
    if (source.type === 'confidence')
        return source.scope === 'field' ? source.name : undefined;
    return undefined;
}
export function jsTypeForDataType(dt) {
    switch (dt) {
        case 'text':
        case 'date':
        // Lookup persists the return-column value (typically a string id);
        // dropdown persists the option `value` string. Both compared as strings.
        case 'lookup':
        case 'dropdown':
            return 'string';
        case 'number':
        case 'decimal':
        // Currency is decimal-with-formatting — runtime value is numeric.
        case 'currency':
            return 'number';
        case 'boolean':
            return 'boolean';
    }
}
// ===========================================================================
// Per-condition pure helpers (used by both framework validators and
// validateWorkflowRule)
// ===========================================================================
/**
 * Detects field references in a single condition that don't resolve in the
 * provided field index. Emits one error per missing reference.
 *
 * `surfaceLabel` is the human-readable surface name (e.g. `Rule "X"` or
 * `Workflow rule "Y"`) — passed in by the caller so this helper doesn't
 * depend on `JobTypeVersionContent`.
 */
export function checkFieldMissingOnCondition(cond, path, fieldIndex, surfaceLabel) {
    const out = [];
    if (cond.source.type === 'field') {
        if (!fieldIndex.has(cond.source.name)) {
            out.push({
                code: 'condition.field.missing',
                severity: 'error',
                path: path.map(String),
                message: `${surfaceLabel} references field "${cond.source.name}" in its condition, but no such field exists in this form.`,
                fieldName: cond.source.name,
            });
        }
    }
    else if (cond.source.type === 'field_group') {
        for (const name of cond.source.names) {
            if (!fieldIndex.has(name)) {
                out.push({
                    code: 'condition.field.missing',
                    severity: 'error',
                    path: path.map(String),
                    message: `${surfaceLabel} references field "${name}" in a field-group condition, but no such field exists in this form.`,
                    fieldName: name,
                });
            }
        }
    }
    return out;
}
/**
 * Emits a warn-severity finding when the condition's operator isn't in the
 * compatibility matrix for the source's resolved dataType.
 */
export function checkOperatorInvalidOnCondition(cond, path, fieldIndex, surfaceLabel) {
    const dt = dataTypeOfSource(cond.source, fieldIndex);
    if (dt === null)
        return [];
    const validOps = OPERATOR_COMPATIBILITY[dt];
    if (!validOps || validOps.includes(cond.operator))
        return [];
    return [
        {
            code: 'condition.operator.invalid',
            severity: 'warn',
            path: path.map(String),
            message: `${surfaceLabel} uses operator "${cond.operator}" against ${describeFieldOf(cond.source)} (${dt}). This operator is not typically valid for ${dt} fields and may produce unexpected results.`,
            fieldName: fieldNameOf(cond.source),
        },
    ];
}
/**
 * Emits a warn-severity finding when a literal comparison value's JS type
 * doesn't match the field's expected JS type. Skips operators that don't
 * use a value (`is_empty`, `is_not_empty`) and `matches_regex` (which
 * coerces by design).
 */
export function checkValueTypeMismatchOnCondition(cond, path, fieldIndex, surfaceLabel) {
    if (NO_VALUE_OPERATORS.has(cond.operator))
        return [];
    if (cond.operator === 'matches_regex')
        return [];
    if (cond.value.type !== 'literal')
        return [];
    const dt = dataTypeOfSource(cond.source, fieldIndex);
    if (dt === null)
        return [];
    const literal = cond.value.value;
    const actualJsType = typeof literal;
    const expectedJsType = jsTypeForDataType(dt);
    if (actualJsType === expectedJsType)
        return [];
    return [
        {
            code: 'condition.value.type-mismatch',
            severity: 'warn',
            path: path.map(String),
            message: `${surfaceLabel} compares ${describeFieldOf(cond.source)} (${dt}) to a ${actualJsType} literal "${literal}". The value type does not match the field's dataType — comparison may coerce unexpectedly.`,
            fieldName: fieldNameOf(cond.source),
        },
    ];
}
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * A date-list token can only match if it is a real ISO `YYYY-MM-DD` date. The
 * runtime resolves an `in`/`not_in` `list` value to its RAW tokens (see
 * rules-engine resolvers.ts — `list` returns `value.values` verbatim) and
 * string-equals each against the field value; it never resolves relative
 * tokens (`today±N`) inside a list, and every runtime date producer emits ISO.
 * So anything that isn't a calendar-valid ISO date is a token that can never
 * match. Calendar validity (not just shape) is checked so `2026-02-30` — a
 * typo the field's shape-only format wouldn't catch — is still flagged.
 */
function isValidIsoDate(token) {
    const m = ISO_DATE_RE.exec(token);
    if (!m)
        return false;
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const dt = new Date(Date.UTC(y, mo - 1, d));
    return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
}
/** A numeric-list token can only match a numeric field if it parses as a finite
 *  number. Blank/whitespace parses to 0 via `Number`, so guard it explicitly. */
function isNumericToken(token) {
    return token.trim() !== '' && Number.isFinite(Number(token));
}
const NUMERIC_DATA_TYPES = new Set(['number', 'decimal', 'currency']);
const LIST_MEMBERSHIP_OPERATORS = new Set(['in', 'not_in']);
/**
 * Emits an error-severity finding when an `in`/`not_in` condition compares a
 * date- or number-typed source against a static `list` value containing tokens
 * that can never match (non-ISO dates, non-numeric numbers). One finding per
 * condition, naming every offending token. Text/boolean/lookup/dropdown fields
 * carry no intrinsic token format and are skipped; dynamic `lookup`-sourced
 * lists (DB data, not author-typed) are skipped — only static `list` values are
 * statically checkable. Ledger item (d).
 */
export function checkListValueTokensOnCondition(cond, path, fieldIndex, surfaceLabel) {
    if (!LIST_MEMBERSHIP_OPERATORS.has(cond.operator))
        return [];
    if (cond.value.type !== 'list')
        return [];
    const dt = dataTypeOfSource(cond.source, fieldIndex);
    if (dt === null)
        return [];
    let isValid;
    let hint;
    if (dt === 'date') {
        isValid = isValidIsoDate;
        hint = 'Date values must be ISO YYYY-MM-DD (relative tokens like today+N are not resolved inside a list).';
    }
    else if (NUMERIC_DATA_TYPES.has(dt)) {
        isValid = isNumericToken;
        hint = 'Numeric values only (no thousands separators or symbols).';
    }
    else {
        return [];
    }
    const bad = cond.value.values.filter((t) => !isValid(t));
    if (bad.length === 0)
        return [];
    return [
        {
            code: 'condition.value.list-invalid',
            severity: 'error',
            path: path.map(String),
            message: `${surfaceLabel} lists value(s) for its "${cond.operator}" check on ${describeFieldOf(cond.source)} (${dt}) that can never match: ${bad.map((t) => `"${t}"`).join(', ')}. ${hint}`,
            fieldName: fieldNameOf(cond.source),
        },
    ];
}
/**
 * Emits an error-severity finding when a field_group aggregate is applied
 * to a child whose dataType doesn't satisfy the aggregate's input
 * requirement. First-offender-wins per condition (no duplicates).
 */
export function checkFieldGroupAggregateOnCondition(cond, path, fieldIndex, surfaceLabel) {
    if (cond.source.type !== 'field_group')
        return [];
    const requirement = AGGREGATE_REQUIRES_TYPE[cond.source.aggregate];
    if (requirement === 'any')
        return [];
    for (const name of cond.source.names) {
        const f = fieldIndex.get(name);
        if (!f)
            continue; // missing fields handled by checkFieldMissingOnCondition
        if (!requirement.includes(f.dataType)) {
            return [
                {
                    code: 'condition.field-group.aggregate.invalid',
                    severity: 'error',
                    path: path.map(String),
                    message: `${surfaceLabel} uses aggregate "${cond.source.aggregate}" over fields [${cond.source.names.join(', ')}], but field "${f.name}" has dataType "${f.dataType}" — aggregate "${cond.source.aggregate}" requires ${requirement.join(' or ')} fields.`,
                    fieldName: f.name,
                },
            ];
        }
    }
    return [];
}
/**
 * Validates a `confidence` condition source: the threshold must be a number in
 * (0, 1], and a field-scope check must name a field. `below`/scope live on the
 * source, so no fieldIndex is needed.
 */
export function checkConfidenceSourceOnCondition(cond, path, surfaceLabel) {
    if (cond.source.type !== 'confidence')
        return [];
    const src = cond.source;
    const out = [];
    if (typeof src.below !== 'number' || Number.isNaN(src.below) || src.below <= 0 || src.below > 1) {
        out.push({
            code: 'condition.confidence.invalid',
            severity: 'error',
            path: path.map(String),
            message: `${surfaceLabel} has a confidence check whose threshold must be a number between 0 and 1 (got ${String(src.below)}).`,
        });
    }
    if (src.scope === 'field' && !src.name) {
        out.push({
            code: 'condition.confidence.invalid',
            severity: 'error',
            path: path.map(String),
            message: `${surfaceLabel} has a confidence check on a specific field, but no field is selected.`,
        });
    }
    return out;
}
// ===========================================================================
// Public: validateWorkflowRule
// ===========================================================================
const KNOWN_DATA_TYPES = new Set([
    'text',
    'number',
    'decimal',
    'date',
    'boolean',
    // WU-1 (2026-05-09) re-broadening — without these entries fields with the
    // 3 new dataTypes silently null-out at coerceFieldForIndex and every
    // type-dependent check (operator-matrix, literal-vs-dataType) skips.
    'currency',
    'lookup',
    'dropdown',
]);
/**
 * Coerces an external field summary (with `dataType: string`) into a
 * shape compatible with `FieldContent` for the per-condition helpers.
 * Unknown dataType strings become `null`, which propagates through
 * `dataTypeOfSource` so type-dependent checks skip safely.
 */
function coerceFieldForIndex(f) {
    const dt = KNOWN_DATA_TYPES.has(f.dataType)
        ? f.dataType
        : null;
    // FieldContent has additional fields not used by the per-condition helpers
    // (label, groupId, etc.). The helpers read only `name` + `dataType`; we
    // intentionally cast through unknown to satisfy TypeScript without
    // synthesizing fake values for the unused fields.
    return { name: f.name, dataType: dt };
}
/**
 * Detects condition-staleness findings in a single WorkflowRuleDTO. Path
 * discriminator on emitted errors: `['workflow-rules', rule.id, 'conditions', ...]`.
 *
 * Reuses the same per-condition helpers as the form-config framework
 * validators, so the matrices, error codes, and message shape stay in
 * sync across both rule systems.
 */
export function validateWorkflowRule(rule, fields) {
    const out = [];
    const fieldIndex = new Map(fields.map(f => [f.name, coerceFieldForIndex(f)]));
    const surfaceLabel = `Workflow rule "${rule.name}"`;
    const basePath = ['workflow-rules', rule.id, 'conditions'];
    walkConditionGroup(rule.conditions, basePath, (cond, path) => {
        out.push(...checkFieldMissingOnCondition(cond, path, fieldIndex, surfaceLabel));
        out.push(...checkOperatorInvalidOnCondition(cond, path, fieldIndex, surfaceLabel));
        out.push(...checkValueTypeMismatchOnCondition(cond, path, fieldIndex, surfaceLabel));
        out.push(...checkListValueTokensOnCondition(cond, path, fieldIndex, surfaceLabel));
        out.push(...checkFieldGroupAggregateOnCondition(cond, path, fieldIndex, surfaceLabel));
        out.push(...checkConfidenceSourceOnCondition(cond, path, surfaceLabel));
    });
    return out;
}
// ===========================================================================
// Bundle 3 — non-condition rule-staleness validators
// ===========================================================================
/**
 * Emits a warn-severity finding when the rule has zero actions. Rule will
 * silently no-op when triggered.
 */
export function checkActionsEmptyOnRule(rule, surfaceLabel) {
    if (rule.actions.length > 0)
        return [];
    return [
        {
            code: 'rule.actions.empty',
            severity: 'warn',
            path: ['workflow-rules', rule.id, 'actions'],
            message: `${surfaceLabel} has no actions, so it will do nothing when triggered.`,
        },
    ];
}
/**
 * Emits an error-severity finding for each flagType that appears in BOTH a
 * set_flag and an unset_flag action within the same rule. Race condition at
 * runtime.
 */
export function checkActionsConflictingOnRule(rule, surfaceLabel) {
    const setFlags = new Set(rule.actions.filter(a => a.actionType === 'set_flag').map(a => a.flagType));
    const unsetFlags = new Set(rule.actions.filter(a => a.actionType === 'unset_flag').map(a => a.flagType));
    const conflicts = [...setFlags].filter(f => unsetFlags.has(f));
    return conflicts.map(flagType => ({
        code: 'rule.actions.conflicting',
        severity: 'error',
        path: ['workflow-rules', rule.id, 'actions', flagType],
        message: `${surfaceLabel} has actions that both set and unset flag "${flagType}". Runtime ordering is racy.`,
    }));
}
/**
 * Cross-rule check: emits a warn-severity finding for each active rule
 * sharing a priority+trigger with at least one other active rule. Inactive
 * rules don't count.
 */
export function checkPriorityCollisionOnRules(rules) {
    const active = rules.filter(r => r.isActive);
    const groups = new Map();
    for (const r of active) {
        const key = `${r.trigger} ${r.priority}`;
        const list = groups.get(key) ?? [];
        list.push(r.id);
        groups.set(key, list);
    }
    const out = [];
    for (const [, ids] of groups) {
        if (ids.length < 2)
            continue;
        for (const id of ids) {
            const rule = active.find(r => r.id === id);
            out.push({
                code: 'rule.priority.collision',
                severity: 'warn',
                path: ['workflow-rules', id, 'priority'],
                message: `Workflow rule "${rule.name}" shares priority ${rule.priority} + trigger ${rule.trigger} with ${ids.length - 1} other active rule(s). Firing order is non-deterministic.`,
            });
        }
    }
    return out;
}
/**
 * Validates an entire array of WorkflowRuleDTOs. Combines per-rule checks
 * (conditions + actions) with the cross-rule priority-collision check.
 * Returns all findings as one array; consumers can group by `path[1]`
 * (the rule.id).
 */
export function validateWorkflowRules(rules, fields) {
    const out = [];
    for (const rule of rules) {
        const surfaceLabel = `Workflow rule "${rule.name}"`;
        out.push(...validateWorkflowRule(rule, fields));
        out.push(...checkActionsEmptyOnRule(rule, surfaceLabel));
        out.push(...checkActionsConflictingOnRule(rule, surfaceLabel));
    }
    out.push(...checkPriorityCollisionOnRules(rules));
    return out;
}
// ===========================================================================
// Public: getFindingAction
// ===========================================================================
/**
 * Maps a validator code to verbose, operator-actionable advice. Used by
 * UI surfaces that want to show a "what to do" line under the validator's
 * concise `message`. Unknown codes return a generic fallback.
 */
export function getFindingAction(code) {
    switch (code) {
        case 'condition.field.missing':
            return 'The referenced field was renamed or deleted. Edit this rule to point at an existing field, or restore the field on the Fields tab.';
        case 'condition.operator.invalid':
            return "This operator can't be applied to the field's data type. Pick a compatible operator, or change the field's data type on the Fields tab.";
        case 'condition.value.type-mismatch':
            return "The comparison value doesn't match the field's data type — the condition will never match. Update the value, or change the field's type.";
        case 'condition.value.list-invalid':
            return "One or more values in this in/not-in list can never match: dates must be ISO YYYY-MM-DD (relative tokens like today+N aren't resolved inside a list) and numbers must be plain numerics. Fix or remove the flagged values.";
        case 'condition.field-group.aggregate.invalid':
            return "The aggregate function isn't compatible with the field group's children. Pick a different aggregate, or adjust the field group's child types.";
        case 'rule.actions.empty':
            return 'This rule has no actions, so it will do nothing when triggered. Add at least one action, or delete the rule.';
        case 'rule.actions.conflicting':
            return 'Two actions on this rule both target the same flag (one sets it, the other unsets it). Pick one and remove the other, or split into two rules.';
        case 'rule.priority.collision':
            return "Another active rule shares this priority + trigger. Rule firing order is non-deterministic. Pick a unique priority, or accept that order doesn't matter for these rules.";
        case 'rule.condition.empty':
            return 'This form/batch rule has no condition, so it never fails. Add the invariant that must hold (e.g. Total = sum of line items), or delete the rule.';
        case 'rule.server-data.incomplete':
            return 'A uniqueness or historical-range check needs both a target field and a validation type. Pick a field and re-select the check type.';
        case 'rule.action.route.incomplete':
            return 'A route action needs a target step. Open the rule and pick the pipeline step to route to, or remove the action.';
        case 'rule.action.set-field.incomplete':
            return 'A "Stamp a value" action needs both a target field and a value. Open the rule and pick the field to write and the value to stamp, or remove the action.';
        case 'rule.on-schedule.no-condition':
            return 'An on-schedule rule with no condition fires once for every document when it enters each step. Add a time condition (e.g. minutes-in-step over a threshold) to target only stale documents.';
        case 'rule.balance.unmaterialized-field':
            return 'Balance rules compare against header-declared values. Give this field a populate-at-import behavior sourced from the batch header (header path), or reference a header field instead.';
        case 'condition.confidence.invalid':
            return 'A field-confidence check needs a threshold between 0 and 1, and a specific-field check needs a field selected. Edit the condition to fix the threshold or pick a field.';
        default:
            return 'Edit this rule to resolve the issue.';
    }
}
// ===========================================================================
// Validator: condition.operator.invalid (warn)
// ===========================================================================
function conditionOperatorInvalidValidator(content, _opts) {
    const out = [];
    const fieldIndex = buildFieldIndex(content.fields);
    walkAllConditions(content, (cond, path) => {
        const surfaceLabel = describeSurface(content, path);
        out.push(...checkOperatorInvalidOnCondition(cond, path, fieldIndex, surfaceLabel));
    });
    return out;
}
// ===========================================================================
// Validator: condition.value.type-mismatch (warn)
// ===========================================================================
function conditionValueTypeMismatchValidator(content, _opts) {
    const out = [];
    const fieldIndex = buildFieldIndex(content.fields);
    walkAllConditions(content, (cond, path) => {
        const surfaceLabel = describeSurface(content, path);
        out.push(...checkValueTypeMismatchOnCondition(cond, path, fieldIndex, surfaceLabel));
    });
    return out;
}
// ===========================================================================
// Validator: condition.field-group.aggregate.invalid (error)
// ===========================================================================
function conditionListValueInvalidValidator(content, _opts) {
    const out = [];
    const fieldIndex = buildFieldIndex(content.fields);
    walkAllConditions(content, (cond, path) => {
        const surfaceLabel = describeSurface(content, path);
        out.push(...checkListValueTokensOnCondition(cond, path, fieldIndex, surfaceLabel));
    });
    return out;
}
function conditionFieldGroupAggregateInvalidValidator(content, _opts) {
    const out = [];
    const fieldIndex = buildFieldIndex(content.fields);
    walkAllConditions(content, (cond, path) => {
        const surfaceLabel = describeSurface(content, path);
        out.push(...checkFieldGroupAggregateOnCondition(cond, path, fieldIndex, surfaceLabel));
    });
    return out;
}
// ===========================================================================
// Validator: condition.confidence.invalid (error)
// ===========================================================================
function conditionConfidenceInvalidValidator(content, _opts) {
    const out = [];
    walkAllConditions(content, (cond, path) => {
        const surfaceLabel = describeSurface(content, path);
        out.push(...checkConfidenceSourceOnCondition(cond, path, surfaceLabel));
    });
    return out;
}
// ===========================================================================
// Slice 4b — Rule completeness checks
// ===========================================================================
/**
 * Per-rule completeness checks (Slice 4b):
 * - form/batch rules need a non-empty condition (else they silently pass);
 * - server-data rules (uniqueness/historicalRange, by type OR by
 *   config.validationType) need a fieldRef AND a config.validationType.
 * Errors block publish — a misconfigured invariant is worse than a no-op.
 */
export function checkRuleCompleteness(rule) {
    const out = [];
    const label = `Rule "${rule.name || '(untitled)'}"`;
    if ((rule.scope === 'form' || rule.scope === 'batch') && !isActionType(rule.type)) {
        const empty = !rule.condition || rule.condition.conditions.length === 0;
        if (empty) {
            out.push({
                code: 'rule.condition.empty',
                severity: 'error',
                path: ['rules', rule.id, 'condition'],
                message: `${label} is ${rule.scope}-scope but has no condition. It would silently pass for every document. Add the invariant that must hold.`,
            });
        }
    }
    const vt = String(rule.config.validationType);
    const isServerData = isServerDataType(rule.type) || isServerDataType(vt);
    if (isServerData) {
        const missing = [];
        if (!rule.fieldRef)
            missing.push('a target field');
        if (!isServerDataType(vt))
            missing.push('a validation type');
        if (missing.length > 0) {
            out.push({
                code: 'rule.server-data.incomplete',
                severity: 'error',
                path: ['rules', rule.id],
                message: `${label} is a server-data validation but is missing ${missing.join(' and ')}.`,
            });
        }
    }
    if (isActionType(rule.type)) {
        const actions = migrateRuleConfig(rule).actions;
        actions.forEach((a, i) => {
            if (a.type === 'route' && !String(a.config?.targetStep ?? '').trim()) {
                out.push({
                    code: 'rule.action.route.incomplete',
                    severity: 'error',
                    path: ['rules', rule.id, 'actions', String(i)],
                    message: `${label} has a route action with no target step. Pick the step to route to, or remove the action.`,
                });
            }
            if (a.type === 'set_field' &&
                (!String(a.config?.targetField ?? '').trim() || !String(a.config?.value ?? '').trim())) {
                out.push({
                    code: 'rule.action.set-field.incomplete',
                    severity: 'error',
                    path: ['rules', rule.id, 'actions', String(i)],
                    message: `${label} has a "Stamp a value" action that needs a target field and a value.`,
                });
            }
            if (a.type === 'reprioritize' && !Number.isInteger(a.config?.priority)) {
                out.push({
                    code: 'rule.action.reprioritize.incomplete',
                    severity: 'error',
                    path: ['rules', rule.id, 'actions', String(i)],
                    message: `${label} has a "Change priority" action with no whole-number priority. Set the priority value.`,
                });
            }
        });
    }
    if (rule.trigger === 'on-schedule' && isActionType(rule.type)) {
        const empty = !rule.condition || rule.condition.conditions.length === 0;
        if (empty) {
            out.push({
                code: 'rule.on-schedule.no-condition',
                severity: 'warn',
                path: ['rules', rule.id, 'condition'],
                message: `${label} is an on-schedule rule with no condition. It will fire once per document per step-entry. Add a time condition (e.g. document.minutesInStep > 1440), or confirm that's intended.`,
            });
        }
    }
    return out;
}
function ruleCompletenessValidator(content, _opts) {
    const out = [];
    for (const rule of content.rules) {
        if (rule.isActive === false)
            continue;
        out.push(...checkRuleCompleteness(rule));
    }
    return out;
}
// ===========================================================================
// Validator: rule.balance.unmaterialized-field (warn) — R-263
// ===========================================================================
/**
 * R-263 — a batch balance rule's EvalContext `fields` map contains ONLY the
 * materialized header values. A condition that references a non-header field
 * (source or value side) resolves null at runtime — a dead comparison. Warn
 * at publish time.
 *
 * NOTE on field-index keying: `buildFieldIndex` keys by `f.name` and rules
 * reference fields by name. In Phase 1, `internalName === name` (see the
 * internalName comment in FieldContent), so this is correct. If internalName
 * diverges from name in a future phase, this look-up should switch to
 * `f.internalName ?? f.name` — consistent with how all other validators in
 * this file resolve field references.
 */
function balanceRuleMaterializationValidator(content, _opts) {
    const out = [];
    const fieldIndex = buildFieldIndex(content.fields);
    for (const rule of content.rules) {
        if (rule.isActive === false)
            continue;
        if (rule.scope !== 'batch' || !isBalanceTrigger(rule.trigger))
            continue;
        if (!rule.condition)
            continue;
        const label = `Rule "${rule.name || '(untitled)'}"`;
        walkConditionGroup(rule.condition, ['rules', rule.id, 'condition'], (cond, path) => {
            const refs = [];
            if (cond.source.type === 'field')
                refs.push(cond.source.name);
            if (cond.value.type === 'field')
                refs.push(cond.value.name);
            for (const name of refs) {
                const f = fieldIndex.get(name);
                if (!f)
                    continue; // condition.field.missing already covers unknown names
                if (classifyField(f) !== 'header') {
                    out.push({
                        code: 'rule.balance.unmaterialized-field',
                        severity: 'warn',
                        path: path.map(String),
                        message: `${label} is a batch balance rule but references field "${name}", which is not populated from the batch header at import. It will resolve to empty at evaluation time.`,
                        fieldName: name,
                    });
                }
            }
        });
    }
    return out;
}
// ===========================================================================
// Validator: rule.uniqueness.member-missing (error) — P5 T5
// ===========================================================================
/**
 * P5 T5 — a composite uniqueness rule's extra member fields (`config.fields`)
 * must all resolve to real fields in the content, or the runtime composite
 * query silently matches nothing (a dead uniqueness check). Error at publish.
 */
function uniquenessCompositeMemberValidator(content, _opts) {
    const out = [];
    const fieldIndex = buildFieldIndex(content.fields);
    for (const rule of content.rules) {
        if (rule.isActive === false)
            continue;
        const vt = String(rule.config?.validationType ?? rule.type);
        if (vt !== 'uniqueness')
            continue;
        const members = Array.isArray(rule.config?.fields) ? rule.config.fields : [];
        const label = `Rule "${rule.name || '(untitled)'}"`;
        for (const name of members) {
            if (!fieldIndex.get(name)) {
                out.push({
                    code: 'rule.uniqueness.member-missing',
                    severity: 'error',
                    path: ['rules', rule.id, 'config', 'fields'],
                    message: `${label} is a composite uniqueness check but its member field "${name}" does not exist. Remove it or pick a real field.`,
                    fieldName: name,
                });
            }
        }
    }
    return out;
}
// ===========================================================================
// Validator: rule.action.reclassify.unknown-classification (error) — P5 T6
// ===========================================================================
/**
 * P5 T6 — a reclassify action's optional `targetClassification` (direct
 * reclassify) must name an existing form classificationKey, or the runtime
 * stamps a classification no pipeline can serve. Error at publish. Absent
 * target = legacy back-to-classify, nothing to check.
 */
function reclassifyTargetValidator(content, _opts) {
    const out = [];
    // `forms` (with classificationKeys) lives on V3 content only; when it isn't
    // present we cannot resolve the classification set, so skip rather than
    // false-flag every direct reclassify.
    const forms = content.forms;
    if (!forms)
        return out;
    const knownKeys = new Set(forms.map((f) => f.classificationKey));
    for (const rule of content.rules) {
        if (rule.isActive === false)
            continue;
        if (!isActionType(rule.type))
            continue;
        const label = `Rule "${rule.name || '(untitled)'}"`;
        migrateRuleConfig(rule).actions.forEach((a, i) => {
            if (a.type !== 'reclassify')
                return;
            const target = a.config?.targetClassification;
            if (target != null && target !== '' && !knownKeys.has(String(target))) {
                out.push({
                    code: 'rule.action.reclassify.unknown-classification',
                    severity: 'error',
                    path: ['rules', rule.id, 'actions', String(i)],
                    message: `${label} reclassifies to "${String(target)}", which is not a form classification on this job. Pick an existing form or leave it empty to send back to Classify.`,
                });
            }
        });
    }
    return out;
}
// ===========================================================================
// Registration
// ===========================================================================
/**
 * Registers all rule-condition-staleness validators with the shared
 * validator registry. Call once at bootstrap or in test `beforeEach`.
 */
export function registerRuleConditionValidators() {
    registerValidator(conditionFieldMissingValidator);
    registerValidator(conditionValueTypeMismatchValidator);
    registerValidator(conditionListValueInvalidValidator); // ledger item (d)
    registerValidator(conditionOperatorInvalidValidator);
    registerValidator(conditionFieldGroupAggregateInvalidValidator);
    registerValidator(conditionConfidenceInvalidValidator);
    registerValidator(ruleCompletenessValidator); // Slice 4b
    registerValidator(balanceRuleMaterializationValidator); // R-263
    registerValidator(uniquenessCompositeMemberValidator); // P5 T5
    registerValidator(reclassifyTargetValidator); // P5 T6
}
// ===========================================================================
// (Note) All helpers and matrices are now public named exports above.
// The previous `__test_internals__` re-export block was removed in Bundle 2;
// tests should import the helpers directly by name.
// ===========================================================================
//# sourceMappingURL=rule-condition-validators.js.map